/**
 * BalanceSheetTable - Displays financial balance sheet data from BigQuery
 *
 * Shows ACTIVOS, PASIVOS, PATRIMONIO, RESULTADOS with:
 * - Historical period columns
 * - Participation % column
 * - YoY change columns
 * - Interannual change column
 *
 * Also shows INDICADORES FINANCIEROS tab with:
 * - Financial ratios organized by category
 * - S2/S4 displayed as ratios, others as percentages
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchBalanceSheetData,
  formatValue,
  formatRatio,
  getRatioClass,
  formatPeriodHeader,
} from '../../../../../services/balanceSheetService';
import {
  fetchFinancialIndicators,
  formatIndicatorValue,
  formatPeriodHeader as formatIndicatorPeriod,
} from '../../../../../services/financialIndicatorsService';
import {
  BalanceSheetData,
  BalanceSheetRow,
  FinancialIndicatorsData,
} from '../../../../../types/balanceSheet';

interface BalanceSheetTableProps {
  institutionId: string;
  reportPeriod: string;
}

type SectionType = 'activos' | 'pasivos' | 'patrimonio' | 'resultados' | 'indicadores';

export default function BalanceSheetTable({
  institutionId,
  reportPeriod,
}: BalanceSheetTableProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<BalanceSheetData | null>(null);
  const [indicatorsData, setIndicatorsData] = useState<FinancialIndicatorsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [indicatorsLoading, setIndicatorsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [indicatorsError, setIndicatorsError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<SectionType>('activos');

  // Load balance sheet data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const balanceData = await fetchBalanceSheetData(institutionId, reportPeriod);
        setData(balanceData);
      } catch (err) {
        console.error('Error loading balance sheet data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    if (institutionId && reportPeriod) {
      loadData();
    }
  }, [institutionId, reportPeriod]);

  // Load financial indicators data when tab is selected (lazy loading)
  // Track if we've attempted to load indicators to prevent infinite retries
  const [indicatorsLoadAttempted, setIndicatorsLoadAttempted] = useState(false);

  useEffect(() => {
    const loadIndicators = async () => {
      try {
        setIndicatorsLoading(true);
        setIndicatorsError(null);
        const indicators = await fetchFinancialIndicators(institutionId, reportPeriod);
        setIndicatorsData(indicators);
      } catch (err) {
        console.error('Error loading financial indicators:', err);
        setIndicatorsError(err instanceof Error ? err.message : 'Failed to load indicators');
      } finally {
        setIndicatorsLoading(false);
        setIndicatorsLoadAttempted(true);
      }
    };

    // Only load once when switching to indicators tab and haven't attempted yet
    if (activeSection === 'indicadores' && institutionId && reportPeriod && !indicatorsLoadAttempted && !indicatorsLoading) {
      loadIndicators();
    }
  }, [activeSection, institutionId, reportPeriod, indicatorsLoadAttempted, indicatorsLoading]);

  if (loading) {
    return (
      <div className="balance-sheet-table-wrapper">
        <div className="loading-state">
          <p>{t('balanceSheet.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="balance-sheet-table-wrapper">
        <div className="error-state">
          <p>{t('balanceSheet.error', { error })}</p>
          <p className="data-note">{t('balanceSheet.institutionPeriod', { institutionId, reportPeriod })}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="balance-sheet-table-wrapper">
        <div className="empty-state">
          <p>{t('balanceSheet.noData')}</p>
        </div>
      </div>
    );
  }

  // Get rows for the active section (only for balance sheet tabs)
  const rows: BalanceSheetRow[] = activeSection !== 'indicadores'
    ? data.sections[activeSection as keyof typeof data.sections] || []
    : [];

  return (
    <>
      {/* Section Tabs - Outside wrapper so they don't scroll */}
      <div className="section-tabs">
        <button
          className={`section-tab ${activeSection === 'activos' ? 'active' : ''}`}
          onClick={() => setActiveSection('activos')}
        >
          {t('balanceSheet.activos')}
        </button>
        <button
          className={`section-tab ${activeSection === 'pasivos' ? 'active' : ''}`}
          onClick={() => setActiveSection('pasivos')}
        >
          {t('balanceSheet.pasivos')}
        </button>
        <button
          className={`section-tab ${activeSection === 'patrimonio' ? 'active' : ''}`}
          onClick={() => setActiveSection('patrimonio')}
        >
          {t('balanceSheet.patrimonio')}
        </button>
        <button
          className={`section-tab ${activeSection === 'resultados' ? 'active' : ''}`}
          onClick={() => setActiveSection('resultados')}
        >
          {t('balanceSheet.resultados')}
        </button>
        <button
          className={`section-tab ${activeSection === 'indicadores' ? 'active' : ''}`}
          onClick={() => setActiveSection('indicadores')}
        >
          {t('balanceSheet.indicadores')}
        </button>
      </div>

      {/* Scrollable table wrapper */}
      <div className="balance-sheet-table-wrapper">
        {activeSection === 'indicadores' ? (
          // Financial Indicators Table
          indicatorsLoading ? (
            <div className="loading-state">
              <p>{t('balanceSheet.loadingIndicators')}</p>
            </div>
          ) : indicatorsError ? (
            <div className="error-state">
              <p>{t('balanceSheet.error', { error: indicatorsError })}</p>
            </div>
          ) : indicatorsData && indicatorsData.categories.length > 0 ? (
            <table className="balance-sheet-table indicators-table">
              <thead>
                <tr>
                  <th>{t('balanceSheet.indicador')}</th>
                  {indicatorsData.periods.map((period) => (
                    <th key={period}>{formatIndicatorPeriod(period)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {indicatorsData.categories.map((category) => (
                  <React.Fragment key={category.name}>
                    {/* Category header row */}
                    <tr className="indicator-category-row">
                      <td colSpan={indicatorsData.periods.length + 1} className="indicator-category-header">
                        {category.name}
                      </td>
                    </tr>
                    {/* Indicator rows */}
                    {category.indicators.map((indicator) => (
                      <tr key={indicator.indicator_id} className="indicator-row">
                        <td className="cell-account indicator-name">
                          <span className="indicator-id">{indicator.indicator_id}</span>
                          {indicator.indicator_name}
                        </td>
                        {indicatorsData.periods.map((period) => (
                          <td key={period} className="cell-numeric indicator-value">
                            {formatIndicatorValue(indicator.values[period], indicator.indicator_id)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <p>{t('balanceSheet.noIndicatorData')}</p>
            </div>
          )
        ) : (
          // Balance Sheet Table
          <table className="balance-sheet-table">
            <thead>
              <tr>
                <th>{t('balanceSheet.cuenta')}</th>
                {/* Period columns */}
                {data.periods.map((period) => (
                  <th key={period}>{formatPeriodHeader(period)}</th>
                ))}
                {/* Participation column */}
                <th>{t('balanceSheet.participacion')}</th>
                {/* YoY columns */}
                {data.yoy_labels.map((label) => (
                  <th key={label}>{label}</th>
                ))}
                {/* Interannual column */}
                <th>{t('balanceSheet.interanual')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr
                  key={`${row.account_code}-${idx}`}
                  className={row.indent_level === 0 ? 'row-category' : row.is_calculated ? 'row-calculated' : ''}
                >
                  {/* Account name */}
                  <td className={`cell-account indent-${row.indent_level}`}>
                    {row.account_name}
                  </td>

                  {/* Period values */}
                  {data.periods.map((period) => (
                    <td key={period} className="cell-numeric">
                      {formatValue(row.values[period])}
                    </td>
                  ))}

                  {/* Participation % */}
                  <td className="ratio-cell">
                    {row.participation !== undefined ? formatRatio(row.participation) : '-'}
                  </td>

                  {/* YoY changes */}
                  {data.yoy_labels.map((label) => {
                    const value = row.yoy_changes?.[label];
                    return (
                      <td key={label} className={`ratio-cell ${getRatioClass(value)}`}>
                        {formatRatio(value)}
                      </td>
                    );
                  })}

                  {/* Interannual */}
                  <td className={`ratio-cell ${getRatioClass(row.interannual)}`}>
                    {formatRatio(row.interannual)}
                  </td>
                </tr>
              ))}

              {rows.length === 0 && (
                <tr>
                  <td colSpan={data.periods.length + data.yoy_labels.length + 3} className="empty-row">
                    {t('balanceSheet.noDataSection')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="table-footer">
        <p className="data-note">
          Source: BigQuery | Institution: {activeSection === 'indicadores' ? indicatorsData?.institution_name || institutionId : data.institution_name} | Period: {reportPeriod}
        </p>
      </div>
    </>
  );
}
